import React, { useEffect, useState } from "react";
import {
  MerchantAccount,
  SellerGridModel,
  GeneralSettingsModel,
  AddressModel,
  PaymentSettings,
  PaymentMethodDetails,
} from "../../../shared/models";
import VisaLogo from "../../../../../../images/payment/visa-card_logo.svg";
import MasterCardLogo from "../../../../../../images/payment/master-card-black-background.svg";
import DiscoverLogo from "../../../../../../images/payment/discover-icon.svg";
import AmexLogo from "../../../../../../images/payment/amex_card_logo.svg";
import ApplePayLogo from "../../../../../../images/payment/apple-pay-icon.svg";
import GooglePayLogo from "../../../../../../images/payment/gpay-icon.svg";
import BankIcon from "../../../../../../images/payment/ach-bank-with-caption.svg";
import CheckWireIcon from "../../../../../../images/payment/check-wire-card.svg";
import NewSellerPopup from "./NewSellerPopup";
import DeleteSellerPopup from "./DeleteSellerPopup";
import GeneralSettings from "./GeneralSettings";
import PaymentSettingsService from "../PaymentSettingsService";
import CardIcon from "../../../../../../images/sidebar/merchant-accounts-icon-blue.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import { observer } from "mobx-react";
import {
  ActiveInactiveButton,
  CollapsiblePanel,
  CustomLabel,
  EditableField,
  FlexContainer,
  Input,
  ITableHeaders,
  Loader,
  SapphireBlueButton,
  TableContainer,
  TableWithHeaderButtons,
  ToastService,
  ToggleSwitch,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  CountryStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import {
  AuthService,
  MerchantAccountService,
  SellerService,
} from "../../../shared/services";
import { IAPIUpdateAccountSettingsModel } from "../../../shared/interfaces";
import { ApplicationStatus } from "../../../shared/enums";
import {
  DescriptionCell,
  ItemDetailMainWrapper,
  LabelCell,
  MainOuterCard,
  VariantWrapper,
} from "../../../shared/styles";
import Categories from "./Categories";
import { TableGrid } from "../../../shared/components";
import Beneficiaries from "./Beneficiaries";
import styled from "styled-components";
import { firstValueFrom } from "rxjs";
import PromotionsGrid from "./PromotionsGrid";
import { PromotionGridModel } from "../../../shared/models/PromotionModels";
import PromotionService from "../../../shared/services/PromotionService";

type IProps = {
  itemStore: ItemsStore;
  accountId: string;
  rolesAndPermissionStore: RolesAndPermissionStore;
};
interface IEditProps {
  margin?: string;
}

const BasicPlaceholder = styled.div`
  height: 300px;
  width: 100%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 30px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  ${CustomLabel} {
    font-size: 22px;
    color: #4c4c4c;
  }
`;
const FlexTable = styled.div<IEditProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 700px;
  margin: ${(props) => props.margin || "0px"};
  .custom-toggle-switch {
    .react-switch-bg {
      width: 28px !important;
      height: 10px;
    }
    .react-switch-handle {
      height: 20px !important;
      width: 20px !important;
      top: 3px !important;
    }
  }

  &.justify-center {
    justify-content: center;
  }
  &.pd-height-static {
    min-height: 41px;
  }
  .bank-ach-icon {
    img {
      max-width: 55px;
      height: 45px;
    }
  }
  .check-wire-icon {
    img {
      min-width: 36px;
      max-width: 40px;
      height: 22px;
    }
  }
`;
const FlexTableDescription = styled.div`
  width: 70%;
`;
const FlexTableCell = styled.div`
  width: 30%;
  .surcharge-input input {
    width: 87px;
    height: 34px;
    border: 1px solid #aaa;
  }
`;
const DetailPanel = styled.div`
  padding: 20px;
`;

const PaymentLabel = styled.span`
  font-size: 16px;
  margin: 0px 23px 0px 15px;
  width: 94px;
  color: #555;
`;

const PaymentIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-grow: 1;

  img {
    min-width: 35px;
    max-width: 40px;
    height: 23px;
  }
`;

const SurchargeInput = styled.input`
  width: 120px;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  outline: none;
`;

const NoteText = styled.p`
  font-size: 14px;
  color: #555;
  margin-top: 10px;
  font-style: italic;
  font-weight: 400;
`;
const PaySettingsPaymentMethods: React.FC<IProps> = ({
  itemStore,
  accountId,
  rolesAndPermissionStore,
}) => {
  const [data, setData] = useState<MerchantAccount[]>([]);
  const [showLoader, setShowLoader] = React.useState(true);
  const [sellerData, setSellerData] = useState<SellerGridModel[]>([]);
  const [promotionsData, setPromotionsData] = useState<PromotionGridModel[]>(
    []
  );
  const [generalSettings, setGeneralSettings] = useState(
    AccountSettingsStore.generalSettings
  );
  const [editDetails, setEditDetails] = useState({
    isEdit: false,
    value: "",
    id: "",
  });
  const [sellerPopup, setSellerPopup] = useState({
    showEdit: false,
    showNew: false,
    sellerId: "",
    showDelete: false,
  });

  useEffect(() => {
    setGeneralSettings(AccountSettingsStore.generalSettings);
  }, [AccountSettingsStore.generalSettings]);

  useEffect(() => {
    getMerchantAccountsData();
    getSellerDetails();
    getPromotionDetails();
    if (AccountSettingsStore.isLoadingInProgress) {
      AccountSettingsStore.loadGeneralSettings();
    }
  }, []);

  async function getMerchantAccountsData() {
    setShowLoader(true);
    const response = await firstValueFrom(
      MerchantAccountService.getMerchantAccounts(accountId, 1, 99999, "")
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result
        .filter((item) => item.applicationStatus === ApplicationStatus.Active)
        .map((item) => MerchantAccount.deserializeAPIModel(item));
      setData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting merchant accounts data");
    }
  }

  const getSellerDetails = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      SellerService.getSellersGridData(
        AuthService.authData.accountId,
        1,
        "99999",
        ""
      )
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result.map((item) =>
        SellerGridModel.deserializeAPIModel(item)
      );
      setSellerData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting seller details");
    }
  };

  const getPromotionDetails = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      PromotionService.getPromotionsGridData(
        AuthService.authData.accountId,
        1,
        "99999",
        ""
      )
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result.map((item) =>
        PromotionGridModel.deserializeAPIModel(item)
      );
      setPromotionsData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting promotion details");
    }
  };

  function onClickSave() {
    if (!rolesAndPermissionStore.hasManagePermission("Save")) return;
    const isValid = generalSettings.validate();
    let hasPaymentErrors = false;

    const updatedData = data.map((account) => {
      const isValid = account.validate();
      if (!isValid) hasPaymentErrors = true;
      return new MerchantAccount({ ...account });
    });

    if (!isValid) {
      setGeneralSettings(new GeneralSettingsModel({ ...generalSettings }));
      ToastService.showError("Please enter valid data in General Section");
      return;
    }

    setData(updatedData);

    if (hasPaymentErrors) {
      ToastService.showError("Surcharge percentage must be between 1 and 3");
      return;
    }

    const apiModel: IAPIUpdateAccountSettingsModel = {
      accountId: AuthService.authData.accountId,
      paymentSettings: {
        accountId: AuthService.authData.accountId,
        paymentSettings: data.map((account) =>
          account.createPaymentSettingsAPIModel()
        ),
      },
      generalSettings: generalSettings.createApiModel(),
    };
    PaymentSettingsService.updateSettings(apiModel).subscribe({
      next: (response) => {
        if (response.result) {
          AccountSettingsStore.setGeneralSettings(generalSettings);
          getMerchantAccountsData();
          ToastService.showSuccess("Payment settings saved successfully");
        }
      },
      error: () => {
        ToastService.showError("Payment settings save failed");
      },
    });
  }
  const handleToggleChange = (
    type: keyof PaymentSettings,
    value: boolean,
    item: MerchantAccount
  ) => {
    const updatedPaymentSettings = new PaymentSettings({
      ...item.paymentSettings,
      [type]: new PaymentMethodDetails({
        ...item.paymentSettings[type],
        isEnabled: value,
      }),
    });

    const updatedItem = new MerchantAccount({
      ...item,
      paymentSettings: updatedPaymentSettings,
    });

    setData((prevData) =>
      prevData.map((merchantAccount) =>
        merchantAccount.id === item.id ? updatedItem : merchantAccount
      )
    );
  };

  // const handleInputChange = (
  //   key: keyof MerchantAccount,
  //   value: string,
  //   id: string
  // ) => {
  //   setData((prevData) =>
  //     prevData.map((merchantAccount) =>
  //       merchantAccount.id === id
  //         ? new MerchantAccount({ ...merchantAccount, [key]: value })
  //         : merchantAccount
  //     )
  //   );
  // };

  const handleSurchargeChange = (
    type: keyof PaymentSettings,
    value: string,
    item: MerchantAccount,
    isBlur?: boolean
  ) => {
    if (isBlur) {
      value = parseFloat(value).toFixed(2);
    }
    const updatedPaymentSettings = new PaymentSettings({
      ...item.paymentSettings,
      [type]: new PaymentMethodDetails({
        ...item.paymentSettings[type],
        surchargePercentage: value,
      }),
    });

    const updatedItem = new MerchantAccount({
      ...item,
      paymentSettings: updatedPaymentSettings,
    });

    setData((prevData) =>
      prevData.map((merchantAccount) =>
        merchantAccount.id === item.id ? updatedItem : merchantAccount
      )
    );
  };

  // const onSaveName = () => {
  //   if (editDetails.id && editDetails.value) {
  //     handleInputChange(
  //       "statementDescriptor",
  //       editDetails.value,
  //       editDetails.id
  //     );
  //     setEditDetails((details) => ({
  //       ...details,
  //       isEdit: false,
  //       value: "",
  //     }));
  //   }
  // };

  const renderDetails = (item: MerchantAccount, index: number) => {
    const renderPaymentRow = (
      key: keyof PaymentSettings,
      label: string,
      logos: string[],
      isEnabled: boolean,
      isDisabled: boolean,
      surchargePercentage: string,
      paymentIconClass?: string
    ) => {
      const hasError = item.hasError(key);
      return (
        <FlexTable key={key} className="pd-height-static">
          <FlexTableDescription>
            <FlexContainer alignItems="center">
              <ToggleSwitch
                on={isEnabled}
                onChange={(value) => handleToggleChange(key, value, item)}
                disabled={isDisabled}
                height={10}
                offColor="#FF4B55"
              />
              <PaymentLabel>{label}</PaymentLabel>
              <PaymentIcons className={paymentIconClass}>
                {logos.map((logo, i) => (
                  <img key={i} src={logo} alt={label} />
                ))}
              </PaymentIcons>
            </FlexContainer>
          </FlexTableDescription>
          <FlexTableCell>
            {isEnabled && (
              <FlexContainer
                spacing={10}
                alignItems="center"
                className="surcharge-input"
              >
                <Input
                  type="text"
                  value={surchargePercentage || ""}
                  onChange={(e, isBlur) =>
                    handleSurchargeChange(
                      key,
                      e.target.value || "0",
                      item,
                      isBlur
                    )
                  }
                  error={hasError}
                  placeholder="%"
                />
                <CustomLabel>%</CustomLabel>
              </FlexContainer>
            )}
          </FlexTableCell>
        </FlexTable>
      );
    };

    return (
      <VariantWrapper className="category-block payment-details-block">
        <CollapsiblePanel
          key={index}
          title={item.name}
          isOpened={index === 0}
          imgSrc={CardIcon}
          className="settings-sub-header"
        >
          <FlexTable margin="5px 0px 20px">
            <FlexTableDescription>
              <CustomLabel fontSize={"17px"}>
                Select Payment Methods
              </CustomLabel>
            </FlexTableDescription>
            <FlexTableCell>
              <CustomLabel fontSize={"17px"}>Surcharge</CustomLabel>
            </FlexTableCell>
          </FlexTable>
          {item.paymentSettings && (
            <FlexContainer fxLayout="column" spacing={20}>
              {renderPaymentRow(
                "cards",
                "Credit Card",
                [VisaLogo, MasterCardLogo, DiscoverLogo, AmexLogo],
                item.paymentSettings.cards.isEnabled,
                true,
                item.paymentSettings.cards.surchargePercentage
              )}
              {renderPaymentRow(
                "applePay",
                "Apple Pay",
                [ApplePayLogo],
                item.paymentSettings.applePay.isEnabled,
                true,
                item.paymentSettings.applePay.surchargePercentage
              )}
              {renderPaymentRow(
                "googlePay",
                "Google Pay",
                [GooglePayLogo],
                item.paymentSettings.googlePay.isEnabled,
                true,
                item.paymentSettings.googlePay.surchargePercentage
              )}
              {renderPaymentRow(
                "bankAch",
                "Bank / ACH",
                [BankIcon],
                item.paymentSettings.bankAch.isEnabled,
                true,
                item.paymentSettings.bankAch.surchargePercentage,
                "bank-ach-icon"
              )}
              {renderPaymentRow(
                "checkWire",
                "Check / Wire",
                [CheckWireIcon],
                item.paymentSettings.checkWire.isEnabled,
                true,
                item.paymentSettings.checkWire.surchargePercentage,
                "check-wire-icon"
              )}
            </FlexContainer>
          )}

          {/* <FlexContainer
              alignItems="center"
              spacing={20}
              margin="0px 0px 20px 0px"
            >
              <CustomLabel>Statement Descriptor</CustomLabel>
              <EditableField
                value={
                  (editDetails.id === item.id && editDetails.value) ||
                  item.statementDescriptor
                }
                isEdit={editDetails.isEdit && editDetails.id === item.id}
                onEditClick={() => {
                  if (!rolesAndPermissionStore.hasManagePermission("Edit"))
                    return;
                  setEditDetails({
                    ...editDetails,
                    isEdit: true,
                    value: item.statementDescriptor,
                    id: item.id,
                  });
                }}
                onSaveClick={onSaveName}
                onCancelClick={() =>
                  setEditDetails((details) => ({
                    ...details,
                    value: "",
                    isEdit: false,
                  }))
                }
                onChange={(e) =>
                  setEditDetails((details) => ({
                    ...details,
                    value: e.target.value,
                  }))
                }
                placeholder="Enter Name"
              />
            </FlexContainer> */}

          <FlexTable className="justify-center" margin="20px 0px 40px">
            {item.paymentSettings.googlePay.isEnabled ||
              (item.paymentSettings.applePay.isEnabled && (
                <NoteText>
                  NOTE: Apple Pay and Google Pay are not available for recurring
                  transactions.
                </NoteText>
              ))}
          </FlexTable>
          {/* </FlexContainer> */}
        </CollapsiblePanel>
      </VariantWrapper>
    );
  };

  const onClickCancel = () => {
    setGeneralSettings(AccountSettingsStore.generalSettings);
    getMerchantAccountsData();
  };

  const handleSellerEdit = (data: SellerGridModel) => {
    setSellerPopup({
      showEdit: true,
      showNew: false,
      sellerId: data.id,
      showDelete: false,
    });
  };

  const handleSellerDelete = (data: SellerGridModel) => {
    setSellerPopup({
      showEdit: false,
      showNew: false,
      sellerId: data.id,
      showDelete: true,
    });
  };

  function columns() {
    const columns: ITableHeaders<SellerGridModel>[] = [
      {
        dataKey: "isActive",
        title: "Status",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: SellerGridModel) => {
          return (
            <ActiveInactiveButton
            //  minWidth="70px"
            >
              {data.isActive ? "Active" : "Inactive"}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "name",
        title: "Name",
        className: "table-col-20",
        cellClassName: "table-col-20",
        onCellRender: (data: SellerGridModel) => {
          return <CustomLabel>{data.name}</CustomLabel>;
        },
      },
      {
        dataKey: "address",
        title: "Location",
        className: "table-col-30",
        cellClassName: "table-col-30",
        onCellRender: (data: SellerGridModel) => {
          return (
            <CustomLabel className="overflow-ellipsis">
              {formatAddress(data.address)}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "used",
        title: "Used",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: SellerGridModel) => {
          return <CustomLabel>{data.used ? "Used" : "Not Used"}</CustomLabel>;
        },
      },
      {
        dataKey: "name",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: SellerGridModel) => {
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={10}
            >
              <img
                src={EditIcon}
                alt="edit-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Edit"))
                    return;
                  handleSellerEdit(data);
                }}
                className="img-15 clickable"
              />
              <img
                src={DeleteIcon}
                alt="delete-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Delete"))
                    return;
                  handleSellerDelete(data);
                }}
                className="img-15 clickable"
              />
            </FlexContainer>
          );
        },
      },
    ];
    return columns;
  }

  function formatAddress(address: AddressModel) {
    if (!address) return "";
    return `${addCommaInAddress(address.address1)}${addCommaInAddress(address.address2)}${addCommaInAddress(address.city)}${addCommaInAddress(address.state.name)}${address.country}`;
  }

  function addCommaInAddress(value: string) {
    if (!value) return "";
    return value + ", ";
  }

  if (showLoader || AccountSettingsStore.isLoadingInProgress)
    return <>{new Loader(true).loaderComponent}</>;
  const showTableHeader = sellerData.length > 0 ? true : false;
  return (
    <ItemDetailMainWrapper className="payment-settings-wrapper">
      <MainOuterCard padding="30px 40px" className="ps-main-outer-card">
        <FlexContainer fxLayout="column" spacing={30}>
          <GeneralSettings
            settings={generalSettings}
            updateSettings={(settings: GeneralSettingsModel) => {
              generalSettings.validate(true);
              setGeneralSettings(new GeneralSettingsModel({ ...settings }));
            }}
          />
          <Beneficiaries
            beneficiaries={
              AccountSettingsStore.beneficiaryService.beneficiaries || []
            }
            isLoading={
              AccountSettingsStore.beneficiaryService.isLoadingInProgress
            }
          />
          <Categories
            generalSettings={generalSettings}
            rolesAndPermissionStore={rolesAndPermissionStore}
            handleChange={(settings: GeneralSettingsModel) => {
              generalSettings.validate(true);
              setGeneralSettings(
                new GeneralSettingsModel({
                  ...settings,
                  categories: settings.categories,
                })
              );
            }}
          />
          <CollapsiblePanel
            title="Payment Details"
            className="payment-detail-header"
            isOpened={false}
            collapsibleClassName="full-width"
          >
            {data.map((item, index) => renderDetails(item, index))}
            <FlexContainer justifyContent="center">
              <CustomLabel textAlign="center">
                Payment Details can be modified for Active Merchant Accounts
                only.
              </CustomLabel>
            </FlexContainer>
          </CollapsiblePanel>
          <CollapsiblePanel
            title="Sellers"
            className="payment-detail-header"
            collapsibleClassName="full-width"
            isOpened={false}
          >
            <TableContainer className="default-table seller-table">
              <TableGrid
                data={sellerData}
                headers={showTableHeader ? columns() : ""}
                isShowPlaceholder={sellerData.length === 0 ? false : true}
                tableHeader={
                  <TableWithHeaderButtons
                    title="Purchase Link"
                    totalItems={20}
                    icon="uj"
                    isShowHeaderButton={true}
                    onNewButtonClick={() => {
                      if (
                        !rolesAndPermissionStore.hasManagePermission("Create")
                      )
                        return;
                      setSellerPopup({ ...sellerPopup, showNew: true });
                    }}
                    showRecentButton={false}
                    description="Create one or more Sellers with options and attribute purchases on Checkout Pages."
                  />
                }
                cellSpacing={20}
                //pagination={purchaseLinkStore.pagination}
                totalItemCount={20}
              />
              {/* {!showTableHeader && sellerData.length === 0 && (
                <BasicPlaceholder>
                  <CustomLabel>No Sellers found</CustomLabel>
                  <SapphireBlueButton
                    primary
                    onClick={() => onClickSave()}
                    height="40px"
                  >
                    Create New
                  </SapphireBlueButton>
                </BasicPlaceholder>
              )} */}
            </TableContainer>
          </CollapsiblePanel>
          <PromotionsGrid
            data={promotionsData}
            rolesAndPermissionStore={rolesAndPermissionStore}
            onRefresh={(refresh: boolean) => {
              if (refresh) {
                getPromotionDetails();
              }
            }}
          />
          <div className="full-width">
            <FlexContainer
              alignItems="center"
              spacing={30}
              justifyContent="center"
            >
              <SapphireBlueButton onClick={() => onClickCancel()} height="40px">
                Cancel
              </SapphireBlueButton>
              <SapphireBlueButton
                primary
                onClick={() => onClickSave()}
                height="40px"
              >
                Save
              </SapphireBlueButton>
            </FlexContainer>
          </div>
        </FlexContainer>
      </MainOuterCard>
      {sellerPopup.showNew && (
        <NewSellerPopup
          onClose={(refresh: boolean, sellerId: string) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showNew: false });
          }}
          countryStore={new CountryStore()}
        />
      )}
      {sellerPopup.showEdit && (
        <NewSellerPopup
          isEdit
          sellerId={sellerPopup.sellerId}
          onClose={(refresh: boolean, sellerId: string) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showEdit: false });
          }}
          countryStore={new CountryStore()}
        />
      )}
      {sellerPopup.showDelete && (
        <DeleteSellerPopup
          sellerId={sellerPopup.sellerId}
          onClose={(refresh: boolean) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showDelete: false });
          }}
          sellerData={sellerData.find(
            (seller) => seller.id === sellerPopup.sellerId
          )}
        />
      )}
    </ItemDetailMainWrapper>
  );
};

export default observer(PaySettingsPaymentMethods);
